'use client';

import RequiredConsentsModal from '@/basket/components/login/required-consents/required-consents-modal';
import Button from '@/components/base/button/button';
import { useSocialMedialLoginMutation } from '@/features/basket/api/mutation/social-media-login';
import { useLoginBySocialMedia } from '@/features/basket/components/login/hooks';
import { CallbackActions } from '@/features/login/components/login/log-in-with-email';
import {
  type LoginConsentStore,
  useLoginConsentStore,
} from '@/features/login/store/login-consents-store';
import { getCorrectPathAfterLogin } from '@/features/login/utils/get-correct-path-after-login';
import { useSetCookie } from '@/hooks/use-cookies';
import { useRouter } from '@/navigation';
import { ACCOUNT_ROUTES, LOGIN_ROUTES } from '@/routes';
import { SocialMediaLogin } from '@/types/social-media-login';
import { Cookie, CustomerLoginType, SessionStorage } from '@/types/storage';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import googleLogo from 'public/images/social/google-color.svg';
import { useState, useTransition } from 'react';

const selector = ({ setConsent, getConsent }: LoginConsentStore) => ({
  setConsent,
  getConsent,
});

type LoginMethod = SocialMediaLogin.GOOGLE | 'guest' | 'email' | null;

export const LoginMethods = () => {
  const t = useTranslations('login');
  const tc = useTranslations('common');
  const [loginType, setLoginType] = useState<LoginMethod>(null);
  const [isTransitionPending, startTransition] = useTransition();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { setConsent, getConsent } = useLoginConsentStore(selector);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutateAsync: socialMediaLogin, isPending } =
    useSocialMedialLoginMutation();
  const successRedirect =
    typeof sessionStorage != 'undefined' &&
    sessionStorage.getItem(SessionStorage.ON_SUCCESSFULLY_LOGIN);

  const setCookie = useSetCookie();

  const path = getCorrectPathAfterLogin(
    successRedirect || ACCOUNT_ROUTES.DASHBOARD,
  );
  useLoginBySocialMedia(path, false);

  const provider = searchParams.get('provider') || '';
  const guestLogin = searchParams.get('guestLogin') || '';
  const addToFavorite = searchParams.get(CallbackActions.ADD_TO_FAVORITE) ?? '';

  const hasAcceptedTerms = () => {
    const terms = getConsent('terms');

    if (!terms) {
      setIsModalOpen(true);

      return false;
    }

    return true;
  };

  const handleLoginByGoogle = async () => {
    setLoginType(SocialMediaLogin.GOOGLE);

    if (!hasAcceptedTerms()) return;

    const { redirectUrl } = await socialMediaLogin({
      type: SocialMediaLogin.GOOGLE,
      callOutPath: LOGIN_ROUTES.LOGIN.replace('/', ''),
    });
    setCookie(Cookie.CUSTOMER_LOGIN_TYPE, CustomerLoginType.SOCIAL);
    startTransition(() => {
      router.push(redirectUrl);
    });
  };

  const loginEmailHref = searchParams.has(CallbackActions.ADD_TO_FAVORITE)
    ? `${LOGIN_ROUTES.LOGIN_EMAIL}?addToFavorite=${addToFavorite}`
    : LOGIN_ROUTES.LOGIN_EMAIL;

  const handleRedirectToEmailLogin = () => {
    setLoginType('email');
    if (!hasAcceptedTerms()) return;

    startTransition(() => {
      router.push(loginEmailHref);
    });
  };
  const handleRedirectToGuestLogin = () => {
    setLoginType('guest');
    if (!hasAcceptedTerms()) return;

    startTransition(() => {
      router.push(LOGIN_ROUTES.LOGIN_GUEST);
    });
  };

  const onConfirmTerms = async () => {
    switch (loginType) {
      case 'google': {
        handleLoginByGoogle();
        break;
      }
      case 'email': {
        handleRedirectToEmailLogin();
        break;
      }
      case 'guest': {
        handleRedirectToGuestLogin();
        break;
      }
    }
  };

  const isLoadingState = isTransitionPending || isPending;

  const setRequiredConsents = () => {
    setConsent('terms', true);
  };

  return (
    <div className="flex flex-col py-m">
      <div className="flex flex-col gap-l no-flex-gap:space-y-l py-m">
        <Button
          variant="primary"
          className="flex w-full items-center justify-center text-center"
          onClick={handleRedirectToEmailLogin}
          testId="login_email_button"
          isLoading={isTransitionPending && loginType === 'email'}
          disabled={
            (isTransitionPending && loginType !== 'email') || !!provider
          }
        >
          {t('btn_continue_email')}
        </Button>
        <div className="flex flex-col gap-xs">
          <Button
            variant="secondary"
            className="w-full"
            onClick={handleLoginByGoogle}
            role="button"
            testId="login_google_button"
            isLoading={
              provider === SocialMediaLogin.GOOGLE ||
              (isTransitionPending && loginType === 'google')
            }
            disabled={isTransitionPending && loginType !== 'google'}
          >
            <div className="flex justify-center gap-xxs text-center">
              <Image
                src={googleLogo}
                alt="Google logo"
                width={24}
                height={24}
              />
              {t('btn_continue_google')}
            </div>
          </Button>
        </div>
      </div>
      {guestLogin && (
        <div>
          <div className="flex w-full items-center justify-center py-m">
            <span className="h-px w-full bg-[theme(textColor.default)]" />
            <div className="px-m text-body text-muted">{tc('or')}</div>
            <span className="h-px w-full bg-[theme(textColor.default)]" />
          </div>
          <Button
            variant="secondary"
            className="flex w-full items-center justify-center text-center"
            onClick={handleRedirectToGuestLogin}
            testId="login_guest_button"
            isLoading={isTransitionPending && loginType === 'guest'}
            disabled={
              (isTransitionPending && loginType !== 'guest') || !!provider
            }
          >
            {t('btn_continue_guest')}
          </Button>
        </div>
      )}
      <RequiredConsentsModal
        onConfirm={onConfirmTerms}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isPending={isLoadingState}
        setRequiredConsents={setRequiredConsents}
      />
    </div>
  );
};
